import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { BodyExtraSmall, BodyRegularSemiBold, Heading4, Heading6 } from '@src/fonts/typography/Typography';
import CardIcon from '@src/icons/secure_payment.svg';
import { useAppDispatch, useAppSelector, useTranslate, useWidgetParam } from '@src/core/hooks';
import { setStepCheckout } from '@src/stores/checkout';
import { ClassNamespace, extractValues } from '@src/common';
import { Order, Sale, SaleChange, PaymentOption, DirectLink } from '@src/core/buyerApiClient';
import React, { FormEvent, useEffect, useMemo } from 'react';
import { CardPaymentForm } from '@src/features/payments/legacy/CardPaymentForm';
import { PixPaymentForm } from '@src/features/payments/legacy/PixPaymentForm';
import { device } from '@src/common/breakpoints';
import { PurchaseResume } from '@src/features/orders/legacy';
import type { PurchaseSummary } from '@src/common/types/purchaseSummary';
import { PaymentMethodFormProps } from '@src/features/payments/types';
import { useWidgetId } from '@src/core/IdProvider';
import {
  paymentErrorsSelector,
  paymentOngoingSelector,
  processPayment,
  getPaymentStatus,
} from '@src/features/payments/store/payment';
import { usePaymentOptions } from '@src/features/payments/options';
import {
  buildPaymentCardParamsFromDom,
  isCreditCardOption,
  needsPhoneVerificationUnlessPaymentLink,
  PaymentMethodFormKind,
  resolvePaymentMethodForm,
} from '@src/domains/payments';
import { DirectLinkType } from '@src/domains/direct-links';
import { isTransactablePayable, resolveTransactableEntity } from '@src/domains/transactables';
import { track } from '@src/services/analytics';
import { PhoneVerificationAlert } from '@src/features/customers/auth/phone-verification';
import { buyerIsForeign, buyerSelector } from '@src/features/customers';
import { ProviderFinancialError } from '@src/features/payments';
import Loading from '@src/components/ui2/Loading';
import { ExternalAuthenticationSetup } from '@src/features/payments/external-auth';
import ForeignAlert from '@src/components/Checkout/ForeignAlert';

interface PaymentFormProps {
  cn?: ClassNamespace;
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
  saleChange?: SaleChange;
  // Composed across orders and sales, which only a view can do. It arrives as a prop so this feature
  // stays portable to any view that renders it — importing the view's hook would invert views/ -> features/.
  purchaseSummary: PurchaseSummary;
}

const PaymentForm = (props: PaymentFormProps) => {
  const { cn, order, sale, directLink, saleChange, purchaseSummary } = props;
  // Sale and order links show their summary beside the form; the cart checkout and payment links do not.
  const showResume = Boolean(sale) || directLink?.type === DirectLinkType.Order;

  const dispatch = useAppDispatch();
  const { i18nElement, i18n } = useTranslate();
  const widgetId = useWidgetId();
  const inputClass = useWidgetParam('input-class') || 'input';
  const [buyer] = useAppSelector(buyerSelector);

  const paymentOngoing = useAppSelector(paymentOngoingSelector);
  const errors = useAppSelector(paymentErrorsSelector);
  const isForeignBuyer = useAppSelector(buyerIsForeign);

  const transactable = {
    order,
    sale,
    directLink,
    saleChange,
  };

  const {
    options: validMethods,
    loading: isLoadingPaymentOptions,
    selected: selectedPaymentOption,
    select: setSelectedPaymentOption,
  } = usePaymentOptions(transactable);

  const showForeignAlert = useMemo(
    () => isForeignBuyer && isCreditCardOption(selectedPaymentOption),
    [isForeignBuyer, selectedPaymentOption],
  );

  const handlePaymentSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const { paymentMethodId, cardValidity, ...remaining } = extractValues(ev.target as HTMLFormElement);

    const paymentCard = buildPaymentCardParamsFromDom(
      Object.keys(remaining).length > 0 ? { ...remaining, validity: cardValidity } : {},
    );

    track('checkoutPaymentAttempt');

    dispatch(
      processPayment({
        order,
        sale,
        directLink,
        saleChange,
        paymentMethodId: `${paymentMethodId}`,
        paymentCard,
      }),
    );
  };

  const isSelected = (opt: PaymentOption) =>
    selectedPaymentOption != undefined && opt.paymentMethod.id === selectedPaymentOption.paymentMethod.id;

  const needsPhoneVerification = needsPhoneVerificationUnlessPaymentLink({
    paymentOption: selectedPaymentOption,
    phoneVerifiedAt: buyer?.phoneVerifiedAt,
    directLinkType: directLink?.type,
  });

  // Nothing to pay is not the same as expired: with no entity there is no purchase to declare over.
  const isExpired = Boolean(resolveTransactableEntity(transactable)) && !isTransactablePayable(transactable);

  useEffect(() => {
    track('checkoutPaymentFormAccess');

    dispatch(setStepCheckout({ id: widgetId || '', value: 'payment' }));
  }, []);

  return (
    <>
      {order || sale || saleChange ? (
        <PurchaseResumeResponsive>
          <PurchaseResume
            summary={purchaseSummary}
            saleChange={saleChange}
            isResponsive
            paymentOption={selectedPaymentOption}
          />
        </PurchaseResumeResponsive>
      ) : null}

      <PaymentContainer data-cy='checkout_paymentForm'>
        <ContainerHeader>
          <HeaderTitle>{i18nElement('checkout_step_payment')}</HeaderTitle>
          <HeaderSecurityNote>
            <SecurePaymentIcon>
              <CardIcon />
            </SecurePaymentIcon>
            <SecurePaymentNote>{i18nElement('checkout_step_payment_security_note')}</SecurePaymentNote>
          </HeaderSecurityNote>
        </ContainerHeader>

        <ContainerBody>
          <PaymentForms showResume={showResume}>
            {isExpired ? (
              <ExpiredSales>
                <p>
                  <strong>{i18nElement('checkout_expiredSale')}</strong>
                </p>
                <p>{i18nElement('checkout_makeYourPurchaseAgain')}</p>
              </ExpiredSales>
            ) : validMethods.length > 0 ? (
              <PaymentFormsContainer>
                {paymentOngoing ? <LoadingCardPayment /> : null}

                <PaymentMethods paymentOngoing={paymentOngoing && !directLink}>
                  <MethodHeadContainer>
                    <MethodHead>
                      {validMethods.map((opt) => (
                        <Method
                          key={opt.paymentMethod.id}
                          isSelected={isSelected(opt)}
                          onClick={() => setSelectedPaymentOption(opt)}
                        >
                          <span>{opt.paymentMethod.name}</span>
                          {opt.paymentMethod.discountPercentage ? (
                            <span>{` (${opt.paymentMethod.discountPercentage.toString().replace('.', ',')}${i18n(
                              'checkout_percentDiscount',
                            )})`}</span>
                          ) : null}
                        </Method>
                      ))}
                    </MethodHead>

                    {showForeignAlert ? <ForeignAlert /> : null}
                  </MethodHeadContainer>

                  {needsPhoneVerification ? (
                    <PhoneVerificationBody>
                      <PhoneVerificationAlert />
                    </PhoneVerificationBody>
                  ) : (
                    <MethodBody>
                      {renderPaymentForm({
                        cn,
                        order,
                        sale,
                        directLink,
                        saleChange,
                        paymentOption: selectedPaymentOption,
                        handleSubmit: handlePaymentSubmit,
                        saleStatusRequester: () => dispatch(getPaymentStatus({ directLink, sale, order, saleChange })),
                        errors,
                        inputClass,
                        unavailableMessage: i18n('checkout_unavailablePaymentMethod'),
                      })}
                    </MethodBody>
                  )}
                </PaymentMethods>
              </PaymentFormsContainer>
            ) : isLoadingPaymentOptions ? (
              <Loading size={24} fullWidth={false} />
            ) : (
              <ProviderFinancialError />
            )}
          </PaymentForms>

          {order || sale || saleChange ? (
            <PurchaseResumeDesktop>
              <PurchaseResume summary={purchaseSummary} saleChange={saleChange} paymentOption={selectedPaymentOption} />
            </PurchaseResumeDesktop>
          ) : null}
        </ContainerBody>
      </PaymentContainer>

      {selectedPaymentOption ? <ExternalAuthenticationSetup paymentOption={selectedPaymentOption} /> : null}
    </>
  );
};

const renderPaymentForm = (props: PaymentMethodFormProps) => {
  switch (resolvePaymentMethodForm(props.paymentOption?.paymentMethod.code)) {
    case PaymentMethodFormKind.Card:
      return <CardPaymentForm {...props} />;
    case PaymentMethodFormKind.Pix:
      return <PixPaymentForm {...props} />;
    default:
      return <div>{props.unavailableMessage}</div>;
  }
};

interface PaymentMethodProps {
  isSelected: boolean;
}

interface PaymentMethodsProps {
  paymentOngoing: boolean;
}

interface PaymentFormsProps {
  showResume: boolean;
}

const PaymentContainer = styled.div`
  width: 100%;
  max-width: 1095px;
  height: auto;
  border-radius: 8px;
  padding: 16px;
  background-color: ${LayoutColors.whiteDark};
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${device.mediumTablet} {
    padding: 24px;
  }
`;

const ContainerBody = styled.div`
  display: flex;
  width: 100%;
  gap: 0 16px;
`;

const PaymentForms = styled.div<PaymentFormsProps>`
  ${(props) => (props.showResume ? 'max-width: 733px;' : '')}

  width: 100%;
  position: relative;
  ${device.mediumTablet} {
    ${(props) => (props.showResume ? 'width: calc(100% - 298px);' : '')}
  }
`;

const ExpiredSales = styled.div``;

const PaymentFormsContainer = styled.div`
  width: 100%;
  height: auto;
  position: relative;
`;

const PaymentMethods = styled.div<PaymentMethodsProps>`
  display: flex;
  flex-flow: column;
  ${(props) => (props.paymentOngoing ? 'opacity: 0.5; pointer-events: none;' : '')}
`;

const MethodHead = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;

  ${device.smallTablet} {
    flex-flow: row;
  }
`;

const Method = styled.button<PaymentMethodProps>`
  ${BodyRegularSemiBold};
  width: 100%;
  height: 44px;
  border-radius: 4px;
  text-align: center;
  background-color: ${(props) => (props.isSelected ? LayoutColors.justBlue : 'transparent')};
  border: 1px solid ${LayoutColors.justBlue};
  color: ${(props) => (props.isSelected ? LayoutColors.whiteLight : LayoutColors.justBlue)};
  cursor: pointer;
`;

const MethodBody = styled.div`
  margin-top: 16px;
  border: 1px solid #cdcdcd;
  border-radius: 4px;
  padding: 16px;
`;

const PhoneVerificationBody = styled.div`
  margin-top: 16px;
`;

const ContainerHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${device.smallTablet} {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const HeaderTitle = styled.h1`
  ${Heading6}
  color: ${LayoutColors.blackDark};

  ${device.mediumTablet} {
    ${Heading4};
    color: ${LayoutColors.blackDark};
  }
`;

const HeaderSecurityNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0 8px;

  ${device.smallTablet} {
    max-width: 390px;
  }
`;

const SecurePaymentIcon = styled.div`
  width: 36px;
`;

const SecurePaymentNote = styled.p`
  ${BodyExtraSmall};
`;

const LoadingCardPayment = styled.div`
  width: 82px;
  height: 82px;
  border-radius: 100%;
  border: 5px solid ${LayoutColors.justBlue};
  border-bottom-color: rgba(0, 0, 0, 0);
  animation: Rotate 0.5s linear infinite;
  z-index: 999999999;
  position: absolute;
  left: calc(50% - 41px);
  top: calc(50% - 41px);
  @keyframes Rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const PurchaseResumeDesktop = styled.div`
  width: 298px;
  display: none;
  ${device.mediumTablet} {
    display: initial;
    flex-shrink: 0;
  }
`;

const PurchaseResumeResponsive = styled.div`
  width: 100%;
  display: block;
  ${device.mediumTablet} {
    display: none;
  }
  margin-bottom: 16px;
`;

const MethodHeadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
export default PaymentForm;
